import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { DefaultManagementComponent } from 'src/app/controls/forms/default-management.component';
import { HttpError } from 'src/app/core/http-error';
import { Article } from 'src/app/models/article';
import { Rights } from 'src/app/models/enums/rights';
import { IArticle } from 'src/app/models/interfaces/article.interface';
import { ArticlesService } from 'src/app/services/articles.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { ArticleDetailContext } from './article-detail-context';
import { ArticleDetailComponent } from './article-detail/article-detail.component';
import { ArticlesListComponent } from './articles-list/articles-list.component';

enum DisplayMode {
    List,
    Form,
}

@Component({
    selector: 'app-articles',
    templateUrl: './articles.component.html',
    styleUrls: ['./articles.component.scss'],
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, ArticlesListComponent, DetailHeaderComponent, ArticleDetailComponent],
})
export class ArticlesComponent extends DefaultManagementComponent<IArticle, Article, number> implements OnInit {
    /**
     * Liste des icônes utilisés dans le template
     */
    public faArticle = faShoppingCart;

    /**
     * Liste des enums utilisés dans le template
     */
    public override FormMode = FormMode;
    public override DisplayMode = DisplayMode;

    /**
     * Contexte par défaut du formulaire contenant les données invariantes entre article.
     */
    public defaultContext: ArticleDetailContext = new ArticleDetailContext();

    /**
     * Indique si le bouton d'ajout/Edit doit apparaitre.
     */
    public displayAddbuttom = true;
    public displayEditbuttom = true;

    /**
     * Constructeur.
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param articlesService : Service métier utilisé pour gérer les articles.
     */
    constructor(
        protected override notificationService: NotificationService,
        private articlesService: ArticlesService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService
    ) {
        super(notificationService);
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.displayAddbuttom = this.memoryService.getConnectedUser().hasRight(Rights.ArticleCreate);
        this.displayEditbuttom = this.memoryService.getConnectedUser().hasRight(Rights.ArticleEdit);
    }

    /**
     * Appellé lorsque l'utilisateur selectionne un client.
     * @param article Article selectionné.
     */
    public onArticleSelected(article: Article) {
        this.spinner.show('main-spinner');
        this.createContext();
        this.loadArticle(article.id).subscribe(
            (x) => {
                this.refresh(Article.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher l'article." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param article Article sélectionné.
     */
    public onArticleDisplayed(article: Article) {
        this.spinner.show('main-spinner');
        this.createContext();
        this.loadArticle(article.id).subscribe(
            (x) => {
                this.refresh(Article.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher l'article." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param article Article sélectionné.
     */
    public onArticleEdited(article: Article) {
        this.spinner.show('main-spinner');
        this.createContext();
        // Cas de la mise à jour.
        this.loadArticle(article.id).subscribe(
            (x) => {
                this.refresh(Article.createFormCopy(x), FormMode.Edition);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'éditer l'article." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Créer'.
     */
    public onArticleCreated() {
        this.displayForm(Article.createFormCopy(new Article()), FormMode.Creation);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Dupliquer' dans le menu contextuel.
     * @param article Article sélectionné.
     */
    public onArticleDuplicated(article: Article) {
        let duplicatedArticle = Article.createFormCopy(article);
        duplicatedArticle.id = null;
        this.displayForm(duplicatedArticle, FormMode.Creation);
    }

    /**
     * Appellé lorsque le contenu du formulaire a été sauvegardé.
     * @param Article : Valeur du client retourné par le formulaire.
     */
    public onArticleUpdated(article: Article): void {
        this.spinner.show('main-spinner');
        if (this.context.editedItemId) {
            // Cas de la mise à jour.
            this.articlesService.update(this.context.editedItemId, article).subscribe(
                (x) => {
                    this.refreshWithNotification(Article.createFormCopy(x), 'Article mis à jour', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de mettre à jour l'article." + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        } else {
            // Cas de la création.
            this.articlesService.add(article).subscribe(
                (x) => {
                    this.refreshWithNotification(Article.createFormCopy(x), 'Article créé', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', "Impossible de créer l'article." + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param article Article sélectionné.
     */
    public onArticleDeleted(article: Article) {
        this.spinner.show('main-spinner');
        // Suppression effective de l'article.
        this.articlesService.delete(article.id).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, 'Article supprimé');
                this.itemsList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible de supprimer l'article." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lors de l'export
     */
    public onArticleExported() {
        this.createContext();
        this.itemsList.export('articles.xlsx');
    }

    /**
     * Appellé lors de l'import
     */
    public onArticleImported() {
        this.itemsList.refresh();
    }

    /**
     * Appellé lors de l'aide
     */
    public onArticleHelped() {
        this.itemsList.displayHelpDialog('articles');
    }

    /**
     * Charge les données d'un article.
     * @param ArticleId : Id du client à charger.
     */
    private loadArticle(articleId: number): Observable<Article> {
        return this.articlesService.get(articleId);
    }

    /**
     * Pré-rempli les champs du formulaire.
     * @param Article : Valeur utilisée pour pré-remplir les champs du formulaire.
     */
    protected fillForm(article: Article): ArticleDetailContext {
        let context = this.createContext();
        context.formContext.item = article;
        context.editedItemId = article.id;

        return context;
    }

    /**
     * Créer un nouveau contexte.
     */
    protected createContext(): ArticleDetailContext {
        let context = new ArticleDetailContext();
        return context;
    }
}
